<template>
  <div class="atem-widget" :class="{ 'is-busy': !!pending }">
    <div class="atem-widget__top">
      <select :value="nodeId" class="form-control form-control-sm" :disabled="!!pending" @change="onNodeChange">
        <option value="">-- Node --</option>
        <option v-for="n in nodes" :key="n.id" :value="n.id">{{ n.name }}</option>
      </select>
      <button
        type="button"
        class="btn btn-sm btn-outline-secondary"
        title="Refresh"
        :disabled="!nodeId || !!pending"
        @click="refresh"
      >
        <i class="fas" :class="pending === 'refresh' ? 'fa-spinner fa-spin' : 'fa-sync-alt'" />
      </button>
    </div>

    <div v-if="productId" class="atem-widget__meta">
      <span>{{ productId }}</span>
      <span v-if="transport" class="atem-widget__transport">{{ transport }}</span>
    </div>

    <div v-if="!nodeId" class="atem-widget__empty">Select a node</div>
    <template v-else>
      <div class="atem-widget__bus">
        <div class="atem-widget__bus-label">Preview</div>
        <div class="atem-input-grid">
          <button
            v-for="inp in displayInputs"
            :key="'pv-' + inp.id"
            type="button"
            class="atem-input"
            :class="{
              'is-preview': inp.id === preview,
              'is-pending': pending === `pv:${inp.id}`,
            }"
            :disabled="!!pending"
            @click="setPreview(inp.id)"
          >
            <span class="atem-input__num">{{ inputLabel(inp) }}</span>
            <span class="atem-input__name">{{ inp.shortName || inp.longName || `Input ${inp.id}` }}</span>
          </button>
        </div>
      </div>

      <div class="atem-widget__bus atem-widget__bus--program">
        <div class="atem-widget__bus-label">Program</div>
        <div class="atem-input-grid">
          <button
            v-for="inp in displayInputs"
            :key="'pg-' + inp.id"
            type="button"
            class="atem-input atem-input--program"
            :class="{
              'is-program': inp.id === program,
              'is-pending': pending === `pg:${inp.id}`,
            }"
            :disabled="!!pending"
            @click="take(inp.id)"
          >
            <span class="atem-input__num">{{ inputLabel(inp) }}</span>
            <span class="atem-input__name">{{ inp.shortName || inp.longName || `Input ${inp.id}` }}</span>
          </button>
        </div>
      </div>

      <div class="atem-widget__transport-row">
        <button
          type="button"
          class="btn btn-sm btn-danger atem-cut"
          :disabled="!!pending"
          @click="run('cut', () => cmd('cut'))"
        >
          <i class="fas" :class="pending === 'cut' ? 'fa-spinner fa-spin' : 'fa-cut'" />
          CUT
        </button>
        <button
          type="button"
          class="btn btn-sm btn-warning atem-auto"
          :disabled="!!pending || inTransition"
          @click="run('auto', () => cmd('auto'))"
        >
          <i class="fas" :class="pending === 'auto' ? 'fa-spinner fa-spin' : 'fa-forward'" />
          AUTO
        </button>
      </div>
    </template>

    <div v-if="error" class="atem-widget__error">{{ error }}</div>
  </div>
</template>

<script>
import { mapGetters, mapActions } from 'vuex'

const POLL_MS = 2000
const FALLBACK_INPUTS = [
  { id: 1, shortName: 'HDMI 1', longName: 'HDMI 1' },
  { id: 2, shortName: 'HDMI 2', longName: 'HDMI 2' },
  { id: 3, shortName: 'HDMI 3', longName: 'HDMI 3' },
  { id: 4, shortName: 'HDMI 4', longName: 'HDMI 4' },
]

export default {
  props: {
    config: { type: Object, default: () => ({}) },
    widgetId: { type: String, default: '' },
    tabActive: { type: Boolean, default: true },
  },
  data() {
    return {
      inputs: [],
      program: 0,
      preview: 0,
      productId: '',
      transport: '',
      inTransition: false,
      pending: '',
      error: '',
      pollTimer: null,
    }
  },
  computed: {
    ...mapGetters('mission', ['nodes']),
    nodeId() {
      return this.config.nodeId || ''
    },
    displayInputs() {
      if (this.inputs.length) return this.inputs
      return FALLBACK_INPUTS
    },
  },
  watch: {
    tabActive(active) {
      if (!this.nodeId) return
      if (active) this.startPoll()
      else this.stopPoll()
    },
    nodeId: {
      immediate: true,
      handler(id) {
        this.stopPoll()
        this.resetState()
        if (id) {
          this.refresh()
          if (this.tabActive) this.startPoll()
        }
      },
    },
  },
  beforeDestroy() {
    this.stopPoll()
  },
  methods: {
    ...mapActions('mission', ['sendCommand']),
    ...mapActions('workspace', ['updateWidgetConfig']),
    resetState() {
      this.inputs = []
      this.program = 0
      this.preview = 0
      this.productId = ''
      this.transport = ''
      this.inTransition = false
      this.pending = ''
      this.error = ''
    },
    onNodeChange(e) {
      if (!this.widgetId) return
      this.updateWidgetConfig({ widgetId: this.widgetId, config: { nodeId: e.target.value } })
    },
    inputLabel(inp) {
      if (inp.id >= 1 && inp.id <= 8) return inp.id
      return inp.shortName || inp.id
    },
    startPoll() {
      this.stopPoll()
      this.pollTimer = setInterval(() => {
        if (!this.pending) this.refresh(true)
      }, POLL_MS)
    },
    stopPoll() {
      if (this.pollTimer) {
        clearInterval(this.pollTimer)
        this.pollTimer = null
      }
    },
    parsePayload(res) {
      const msg = res && res.result
      if (!msg) return null
      if (msg.type === 'error' || msg.error) {
        throw new Error(msg.error || 'Command failed')
      }
      let payload = msg.payload
      if (typeof payload === 'string') {
        try {
          payload = JSON.parse(payload)
        } catch (e) {
          return null
        }
      }
      return payload
    },
    async cmd(action, payload = {}) {
      if (!this.nodeId) return null
      const res = await this.sendCommand({
        node_id: this.nodeId,
        adapter: 'atem',
        action,
        payload,
      })
      return this.parsePayload(res)
    },
    applyState(data) {
      if (!data) return
      this.program = data.program || 0
      this.preview = data.preview || 0
      this.productId = data.productId || this.productId
      this.transport = data.transport || this.transport
      this.inTransition = !!data.inTransition
      if (Array.isArray(data.inputs) && data.inputs.length) {
        this.inputs = data.inputs
          .filter((i) => i.id > 0 && i.id < 1000)
          .sort((a, b) => a.id - b.id)
      }
    },
    async run(key, fn) {
      if (this.pending) return
      this.pending = key
      this.error = ''
      try {
        await fn()
        await this.refresh(true)
      } catch (e) {
        this.error = e.response?.data?.message || e.message || 'Command failed'
      } finally {
        this.pending = ''
      }
    },
    async refresh(silent = false) {
      if (!this.nodeId) return
      if (!silent && this.pending) return
      if (!silent) this.pending = 'refresh'
      if (!silent) this.error = ''
      try {
        const data = await this.cmd('getState')
        this.applyState(data)
      } catch (e) {
        if (!silent) {
          this.error = e.response?.data?.message || e.message || 'Refresh failed'
        }
      } finally {
        if (!silent) this.pending = ''
      }
    },
    setPreview(inputId) {
      this.run(`pv:${inputId}`, () => this.cmd('setPreview', { inputId }))
    },
    take(inputId) {
      this.run(`pg:${inputId}`, () => this.cmd('take', { inputId }))
    },
  },
}
</script>

<style scoped>
.atem-widget {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  height: 100%;
  min-height: 0;
}

.atem-widget__top {
  display: flex;
  gap: 0.35rem;
  align-items: center;
}

.atem-widget__top select {
  flex: 1;
  min-width: 0;
}

.atem-widget__meta {
  display: flex;
  justify-content: space-between;
  gap: 0.5rem;
  font-size: 0.7rem;
  color: var(--wc-text-muted, #6c757d);
}

.atem-widget__transport {
  text-transform: uppercase;
  font-weight: 600;
}

.atem-widget__bus-label {
  font-size: 0.65rem;
  font-weight: 700;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: var(--wc-text-muted, #6c757d);
  margin-bottom: 0.25rem;
}

.atem-widget__bus--program .atem-widget__bus-label {
  color: #c0392b;
}

.atem-input-grid {
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 0.35rem;
}

.atem-input {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 3rem;
  padding: 0.35rem 0.2rem;
  border: 2px solid rgba(0, 0, 0, 0.12);
  border-radius: 6px;
  background: var(--wc-surface, #fff);
  cursor: pointer;
  transition: border-color 0.15s, background 0.15s;
}

.atem-input__num {
  font-size: 1rem;
  font-weight: 700;
  line-height: 1.1;
}

.atem-input__name {
  font-size: 0.55rem;
  opacity: 0.75;
  text-align: center;
  line-height: 1.1;
  max-width: 100%;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.atem-input.is-preview {
  border-color: #27ae60;
  background: rgba(39, 174, 96, 0.12);
}

.atem-input.is-program,
.atem-input--program.is-program {
  border-color: #c0392b;
  background: rgba(192, 57, 43, 0.14);
}

.atem-input.is-pending {
  opacity: 0.6;
}

.atem-widget__transport-row {
  display: flex;
  gap: 0.5rem;
  margin-top: 0.15rem;
}

.atem-cut,
.atem-auto {
  flex: 1;
  font-weight: 700;
  letter-spacing: 0.04em;
}

.atem-widget__empty {
  font-size: 0.85rem;
  color: var(--wc-text-muted, #6c757d);
  padding: 0.5rem 0;
}

.atem-widget__error {
  font-size: 0.75rem;
  color: var(--wc-danger, #dc3545);
}

.atem-widget.is-busy .atem-input:not(.is-pending) {
  pointer-events: none;
  opacity: 0.85;
}
</style>
